'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '@/contexts/LanguageContext';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const { language, t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className={`relative bg-gradient-to-br from-primary via-secondary to-brand-ocean text-white overflow-hidden ${className}`}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-purple rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 py-16 lg:py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Icon name="CheckBadgeIcon" size={20} variant="solid" className="text-accent" />
                  <span className="text-sm font-body font-medium">Kuwait's Trusted Moving Partner</span>
                </div>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-headline font-bold leading-tight">
                Your Trusted Moving Partner in Kuwait
              </h1>
              
              <p className="text-lg lg:text-xl text-white/90 font-body">
                24/7 reliability you can count on. Every item matters, every move counts. Experience seamless transitions with Almanara Moving.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  href="/instant-quote"
                  className="flex items-center justify-center space-x-2 px-8 py-4 bg-accent text-slate-800 rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-bold text-lg shadow-soft">

                  <Icon name="CalculatorIcon" size={24} variant="solid" />
                  <span>Get Instant Quote</span>
                </Link>
                
                <a
                  href="tel:69911181"
                  className="flex items-center justify-center space-x-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg hover:bg-white/20 transition-all duration-base font-body font-bold text-lg">

                  <Icon name="PhoneIcon" size={24} variant="solid" />
                  <span className="font-accent">6991 1181</span>
                </a>
              </div>
              
              <div className="flex items-center space-x-6 pt-6">
                <div className="flex items-center space-x-2">
                  <Icon name="ClockIcon" size={24} variant="solid" className="text-accent" />
                  <span className="text-sm font-body">24/7 Available</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="ShieldCheckIcon" size={24} variant="solid" className="text-accent" />
                  <span className="text-sm font-body">Fully Insured</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="StarIcon" size={24} variant="solid" className="text-accent" />
                  <span className="text-sm font-body">5-Star Rated</span>
                </div>
              </div>
            </div>
            
            <div className="relative h-96 lg:h-[500px]">
              <div className="absolute inset-0 bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1ed525a07-1767124259557.png"
                  alt="Professional movers in blue uniforms carefully loading furniture into moving truck with protective blankets"
                  className="w-full h-full object-cover" />

              </div>
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section className={`relative bg-gradient-to-br from-primary via-secondary to-brand-ocean text-white overflow-hidden ${className}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-purple rounded-full filter blur-3xl animate-pulse"></div>
      </div>
      
      <div className="container mx-auto px-4 py-16 lg:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Icon name="CheckBadgeIcon" size={20} variant="solid" className="text-accent" />
                <span className="text-sm font-body font-medium">{t({ en: "Kuwait's Trusted Moving Partner", ar: 'شريكك الموثوق للنقل في الكويت' })}</span>
              </div>
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-headline font-bold leading-tight">
              {t({ en: 'Your Trusted Moving Partner in Kuwait', ar: 'شريكك الموثوق للنقل في الكويت' })}
            </h1>
            
            <p className="text-lg lg:text-xl text-white/90 font-body">
              {t({ en: '24/7 reliability you can count on. Every item matters, every move counts. Experience seamless transitions with Almanara Moving.', ar: 'موثوقية على مدار الساعة يمكنك الاعتماد عليها. كل قطعة مهمة، كل نقلة تحسب. اختبر انتقالات سلسة مع المنارة للنقل.' })}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link
                href="/instant-quote"
                className="flex items-center justify-center space-x-2 px-8 py-4 bg-accent text-slate-800 rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-bold text-lg shadow-soft">

                <Icon name="CalculatorIcon" size={24} variant="solid" />
                <span>{t({ en: 'Get Instant Quote', ar: 'احصل على عرض سعر فوري' })}</span>
              </Link>
              
              <a
                href="tel:69911181"
                className="flex items-center justify-center space-x-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg hover:bg-white/20 transition-all duration-base font-body font-bold text-lg">

                <Icon name="PhoneIcon" size={24} variant="solid" />
                <span className="font-accent">6991 1181</span>
              </a>
            </div>
            
            <div className="flex items-center space-x-6 pt-6">
              <div className="flex items-center space-x-2">
                <Icon name="ClockIcon" size={24} variant="solid" className="text-accent" />
                <span className="text-sm font-body">{t({ en: '24/7 Available', ar: 'متاح ٢٤/٧' })}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="ShieldCheckIcon" size={24} variant="solid" className="text-accent" />
                <span className="text-sm font-body">{t({ en: 'Fully Insured', ar: 'مؤمن بالكامل' })}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="StarIcon" size={24} variant="solid" className="text-accent" />
                <span className="text-sm font-body">{t({ en: '5-Star Rated', ar: 'تقييم ٥ نجوم' })}</span>
              </div>
            </div>
          </div>
          
          <div className="relative h-96 lg:h-[500px]">
            <div className="absolute inset-0 bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1ed525a07-1767124259557.png"
                alt="Professional movers in blue uniforms carefully loading furniture into moving truck with protective blankets"
                className="w-full h-full object-cover" />

            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;